import { useEffect } from 'react';

/**
 * Keeps the app shell above the on-screen keyboard.
 *
 * This is the classic mobile-web chat failure and it is specifically an iOS
 * Safari one. When the keyboard opens, Safari does not shrink the layout
 * viewport — it scrolls the *visual* viewport over the top of it. So `100dvh`
 * stays the full screen height, the flex column keeps its size, and the
 * composer the user just tapped ends up underneath the keyboard, with the last
 * few messages hidden behind it. `dvh` does not help: it tracks the collapsing
 * URL bar, not the keyboard.
 *
 * Detecting the keyboard is the whole difficulty, and the obvious measurement
 * is wrong. `window.innerHeight - visualViewport.height` looks like "height
 * covered by the keyboard", but on iOS that difference is roughly the height of
 * Safari's own bottom toolbar even when nothing is focused — so a first version
 * of this shrank the shell by ~90px on every page load, leaving the bottom nav
 * floating above a gap. Browser chrome and a keyboard are indistinguishable by
 * size alone.
 *
 * What does distinguish them: a keyboard only exists while something editable
 * has focus. So the inset is measured against the visible height observed while
 * unfocused, and is only ever non-zero during a focus. At rest it is always 0,
 * which makes the resting layout identical to having no hook at all — the
 * property this needed after getting it wrong once.
 *
 * Deliberately a CSS variable rather than component state: this fires on every
 * frame of the keyboard animation, and re-rendering the whole chat tree at
 * 60fps to move a box is how you make a phone feel slow.
 */

// A keyboard is never this short. Anything smaller is a toolbar animating, an
// accessory bar, or scroll rubber-banding.
const MIN_KEYBOARD_PX = 90;

function isEditableFocused() {
  const el = document.activeElement;
  if (!el) return false;
  return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable === true;
}

export default function useKeyboardInset() {
  useEffect(() => {
    const vv = window.visualViewport;
    const root = document.documentElement;
    // No visualViewport (older browsers, some desktops): leave the variable at
    // its 0px default and the layout behaves exactly as it did before.
    if (!vv) return undefined;

    let frame = null;
    // The visible height with no keyboard up. Re-read continuously while
    // nothing is focused, so it follows Safari's toolbar collapsing and
    // expanding instead of treating that movement as a keyboard.
    let restHeight = vv.height;

    const apply = () => {
      frame = null;

      if (!isEditableFocused()) {
        restHeight = vv.height;
        root.style.setProperty('--keyboard-inset', '0px');
        root.classList.remove('keyboard-open');
        return;
      }

      const covered = Math.round(restHeight - vv.height);
      const inset = covered >= MIN_KEYBOARD_PX ? covered : 0;
      root.style.setProperty('--keyboard-inset', `${inset}px`);
      root.classList.toggle('keyboard-open', inset > 0);
    };

    const schedule = () => {
      if (frame === null) frame = requestAnimationFrame(apply);
    };

    apply();
    vv.addEventListener('resize', schedule);
    vv.addEventListener('scroll', schedule);
    // focusout can land before the keyboard has finished retracting, so the
    // measurement is re-run rather than trusted immediately.
    window.addEventListener('focusin', schedule);
    window.addEventListener('focusout', schedule);
    return () => {
      vv.removeEventListener('resize', schedule);
      vv.removeEventListener('scroll', schedule);
      window.removeEventListener('focusin', schedule);
      window.removeEventListener('focusout', schedule);
      if (frame !== null) cancelAnimationFrame(frame);
      root.style.removeProperty('--keyboard-inset');
      root.classList.remove('keyboard-open');
    };
  }, []);
}
